// Объяснение недоступности модуля — ОДИН визуальный язык на все причины.
//
// Правило, ради которого компонент существует: «тихая деградация — регресс».
// Если раздел открыт только на чтение, кнопки создания погашены, а пункт меню
// показан неактивным — пользователь обязан прочитать почему. Три разных
// решения (баннер аварии, серый экран, исчезнувшая кнопка) читались бы как три
// разные поломки, поэтому причина рисуется одним компонентом с одной
// геометрией и разным тоном.

import type { Availability, ModuleId, UnavailableReason } from "@studyhub/shared/module";
import { Alert, AlertDescription, AlertTitle } from "@studyhub/ui/components/alert";
import {
	type Icon,
	IconAlertTriangle,
	IconArchive,
	IconCircleOff,
	IconCloudOff,
	IconDeviceMobileOff,
	IconLock,
	IconUnlink,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { moduleLabelKey } from "@/lib/module-availability/resolve";

type AlertTone = "default" | "warning" | "info" | "destructive";

/**
 * Причина → тон и знак.
 *
 * Тон — продуктовое утверждение, а не украшение:
 *   • `killed` — янтарный: это НАША авария, временная, данные целы;
 *   • `external_down` — синий информационный: чужая авария, ждать;
 *   • всё остальное — нейтральный: продуктовое решение, а не поломка. Красный
 *     (`destructive`) не используется нигде: выключенный раздел — не ошибка
 *     пользователя и не потеря данных.
 */
const REASON_PRESENTATION: Record<UnavailableReason, { tone: AlertTone; icon: Icon }> = {
	killed: { tone: "warning", icon: IconAlertTriangle },
	external_down: { tone: "info", icon: IconCloudOff },
	product_disabled: { tone: "default", icon: IconCircleOff },
	dependency_missing: { tone: "default", icon: IconUnlink },
	platform_unsupported: { tone: "info", icon: IconDeviceMobileOff },
	lifecycle_removed: { tone: "default", icon: IconArchive },
	tier_locked: { tone: "default", icon: IconLock },
};

interface ModuleStateNoticeProps {
	moduleId: ModuleId;
	availability: Availability;
	className?: string;
}

/**
 * Плашка над содержимым раздела. Возвращает `null` для доступного модуля и для
 * `teaser` — последний рисуется полноценным замком (`ModuleLockedScreen`),
 * а не строкой сверху: там нужен призыв к действию, а не объяснение.
 */
export function ModuleStateNotice({
	moduleId,
	availability,
	className,
}: ModuleStateNoticeProps): React.ReactElement | null {
	const { t } = useTranslation("layout");

	if (availability.state === "available" || availability.state === "teaser") return null;

	const { tone, icon: NoticeIcon } = REASON_PRESENTATION[availability.reason];
	const labelKey = moduleLabelKey(moduleId);
	const moduleName = labelKey ? t(labelKey) : moduleId;

	return (
		<Alert variant={tone} className={className} data-module={moduleId}>
			<NoticeIcon size={16} />
			<AlertTitle>
				{t(`moduleState.${availability.reason}.title`, { module: moduleName })}
			</AlertTitle>
			<AlertDescription>
				{t(`moduleState.${availability.reason}.description`, { module: moduleName })}
			</AlertDescription>
		</Alert>
	);
}
